import { Component, Injector, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
    AccountMapper,
    AnnualReportProvider,
    onUpdateAnnualReportFailed,
    onUpdateAnnualReportSuccessfully,
} from '@modeso/aumico-lib-annualreport-fe';
import { AumicoCoreLayoutProvider, FIRST_ELEMENT_INDEX, NumberUtils } from '@modeso/aumico-lib-core-fe';
import {
    AnnualReportStep,
    IAccount,
    IAccountMappingTemplate,
    IAnnualReport,
    IAopCcr,
    IAopProfitCalculation,
    IAopProposal,
    IAppropriationOfProfit,
    IFinStatements,
    IStyleOptions,
} from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import Debug from 'debug';
import fastDeepEqual from 'fast-deep-equal';
import { cloneDeep } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { filter, find, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { AOP } from '../../../../../../aumico-lib-annualreport-fe/src/lib/constants/autofill-options';
import { MixpanelService } from '../../../../../../mixpanel/mixpanel.service';
import { MIXPANEL_EVENTS } from '../../../../../../mixpanel/mixpanelEvents.enum';
import { IPrintPreviewMappingValue } from '../../../interfaces/IPrintPreviewMappingValue.interface';
import { DataState } from '../../../shared/enum/datastate.enum';
import { templateConstants } from '../../../shared/template-constants';
import { AnnualReportSubPage } from '../annual-report-sub-page';

const debug = Debug('aumico:project-reports-fe:AnnualReportAOPPage');

const NO_PREVIOUS_DATE_PLACEHOLDER = '-';

@UntilDestroy()
@Component({
    selector: 'aumico-annual-report-aop-page',
    templateUrl: 'aop.page.html',
    styleUrls: ['aop.page.scss'],
})
export class AnnualReportAOPPage extends AnnualReportSubPage implements OnInit, OnChanges {
    mappedBalanceAccounts: IAccount[] = [];
    equityAccounts: IAccount[] = [];
    previousCcr: IAopCcr | undefined;
    previousShowCcrTable: boolean | undefined;
    balanceTemplate: IAccountMappingTemplate | undefined;
    currentAnnualProfit = 0;
    previousAnnualProfit = 0;
    currentAnnualReport: IAnnualReport | undefined;
    private appropriationOfProfitOutput: IAppropriationOfProfit | undefined;
    private appropriationOfProfitPrefilledValueOutput: IAppropriationOfProfit | undefined;
    @Input()
    stylePreferenceOptions: IStyleOptions;
    // refer to finNumber pipe for reference on values
    @Input()
    numberFormat = '1.2-2';

    @Input() removeEmptyLines: boolean;

    currentLanguage = this.getCurrentLanguageWithFallBack();

    @Input() selectedAccount: IPrintPreviewMappingValue | undefined;
    @Input() currentAnnualProfitValueForPrint: number | null = null;
    @Input() previousAnnualProfitValueForPrint: number | null = null;
    @Input() isSavedBefore = false;

    @Input() hasNoPreviousData = false;
    previousProfitCalculation: IAopProfitCalculation | undefined;
    previousProposal: IAopProposal | undefined;

    currentYearDate: string;
    previousYearDate: string;

    private isSaveRequestInProgress = false;

    dataState: DataState = DataState.Loading;

    isPartOfArNavigation$: Observable<boolean>;
    aopDescription: string;
    aopFromAutofill: IAppropriationOfProfit;
    finStatements: IFinStatements[];

    constructor(
        private annualReportProvider: AnnualReportProvider,
        public override aumicoCoreLayoutProvider: AumicoCoreLayoutProvider,
        private accountMapper: AccountMapper,
        injector: Injector,
        protected override actionsListener$: ActionsSubject,
        private readonly mixpanelService: MixpanelService,
        dialog: MatDialog,
    ) {
        super(injector, actionsListener$, aumicoCoreLayoutProvider, dialog);

        this.currentAnnualReportStep = AnnualReportStep.AOP;

        this.isPartOfArNavigation$ = this.annualReportNavigationProvider.isPartOfArNavigation$(AnnualReportStep.AOP);
    }

    override ngOnInit(): void {
        this.mixpanelService.time_event(MIXPANEL_EVENTS.AOP_TIME_SPENT_TILL_COMPLETION);

        super.ngOnInit();

        this.dataState = DataState.Loading;


        this.providerOfAnnualReports
            .getActiveAnnualReportEncryptedId$()
            .pipe(
                find((activeAnnualReportEncryptedId) => activeAnnualReportEncryptedId != null),
                untilDestroyed(this),
            )
            .subscribe((activeAnnualReportEncryptedId) => {
                this.providerOfAnnualReports.dispatchActiveAnnualReport(activeAnnualReportEncryptedId);
            });

        this.annualReportProvider
            // REV-20899 save-side isolation: this whole-report object is persisted
            // verbatim on save, so it MUST be the RAW report. The bridged
            // finStatements would zero a v1 report's inherited legacy reclasses.
            // The DISPLAY finStatements (withLatestFrom below) stay reclass-aware,
            // so AoP values still include reclassifications.
            .getActiveAnnualReport$()
            .pipe(
                withLatestFrom(this.annualReportProvider.getActiveFinStatementsWithReclasses$()),
                map(([activeAnnualReport, finStatements]: [IAnnualReport, IFinStatements[]]) => {
                    if (
                        activeAnnualReport != null &&
                        activeAnnualReport.generalInformation != null &&
                        finStatements.length >= templateConstants.financialStatementMinimumLength &&
                        finStatements[templateConstants.balanceTemplate].templateAndAccountsMapping.template != null &&
                        finStatements[templateConstants.incomeTemplate].templateAndAccountsMapping.template != null
                    ) {
                        return [activeAnnualReport, finStatements];
                    } else {
                        // Return null, so that we we this state can be handeled
                        return [null, finStatements];
                    }
                }),
                tap(([activeAnnualReport, finStatements]: [IAnnualReport, IFinStatements[]]) =>
                    this.handleGetActiveAnnualReport(activeAnnualReport, finStatements),
                ),
                filter(
                    ([activeAnnualReport]) =>
                        activeAnnualReport != null && activeAnnualReport.appropriationOfProfit == null,
                ),
                mergeMap(([activeAnnualReport, finStatements]) => {
                    this.finStatements = finStatements;
                    if (activeAnnualReport.appropriationOfProfit == null) {
                        const autofillOptions = activeAnnualReport.templateReportAutofillOptions?.[AOP];
                        if (autofillOptions != null) {
                            return this.annualReportProvider.getAnnualReportForUploadAndMappingAutoFill$();
                        }
                    }

                    return of(null);
                }),
                tap((autofillAnnualReport: IAnnualReport | null) => {
                    if (autofillAnnualReport?.appropriationOfProfit != null) {
                        this.setBalanceAccountsFromMapping();
                        this.aopDescription = autofillAnnualReport.appropriationOfProfit.aopDescription;
                        this.aopFromAutofill = this.generateAopTemplate(autofillAnnualReport.appropriationOfProfit);
                    }
                }),
                filter((annualReport) => annualReport == null),
                mergeMap(() => this.annualReportProvider.getAnnualReportForBalanceAutoFill$()),
                filter((autofillAnnualReport) => autofillAnnualReport != null),
                untilDestroyed(this),
            )
            .subscribe((autofillAnnualReport: IAnnualReport) => {
                this.handleAnnualReportForAutoFill(autofillAnnualReport);
            });

        this.actionsListener$
            .pipe(ofType(onUpdateAnnualReportSuccessfully), untilDestroyed(this))
            .subscribe(() => this.onSaveSuccessfully());

        this.actionsListener$
            .pipe(ofType(onUpdateAnnualReportFailed), untilDestroyed(this))
            .subscribe(() => this.onSaveFailed());
    }

    private generateAopTemplate(appropriationOfProfit: IAppropriationOfProfit): IAppropriationOfProfit {
        const aop: IAppropriationOfProfit = cloneDeep<IAppropriationOfProfit>(appropriationOfProfit);
        if (aop.profitCalculationOutput != null) {
            aop.profitCalculationOutput.profitEntries = aop.profitCalculationOutput.profitEntries.map((entry) => {
                return {
                    ...entry,
                    currentValue: null,
                    previousValue: null,
                };
            });
            aop.profitCalculationOutput.account = this.getUpdatedAccount(aop.profitCalculationOutput);
        }

        if (aop.proposalOutput != null) {
            aop.proposalOutput.proposalEntries = aop.proposalOutput.proposalEntries.map((entry) => {
                return {
                    ...entry,
                    currentValue: null,
                    previousValue: null,
                };
            });
        }

        return aop;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes['annualProfitValueForPrint'] != null &&
            NumberUtils.isFinite(changes['annualProfitValueForPrint'].currentValue)
        ) {
            this.currentAnnualProfit = NumberUtils.roundNumber(changes['annualProfitValueForPrint'].currentValue);
        }
    }

    onAppropriationOfProfitChange(appropriationOfProfit: IAppropriationOfProfit): void {
        const aop = appropriationOfProfit;
        this.appropriationOfProfitOutput = this.removeUndefinedFields(aop);
    }

    onAppropriationOfProfitPrefilledValueOutputprepared(appropriationOfProfit: IAppropriationOfProfit): void {
        const aop = appropriationOfProfit;
        this.appropriationOfProfitPrefilledValueOutput = this.removeUndefinedFields(aop);
        this.appropriationOfProfitOutput = this.appropriationOfProfitPrefilledValueOutput;
    }

    private handleGetActiveAnnualReport(annualReport: IAnnualReport | null, finStatements: IFinStatements[]): void {
        if (annualReport === null) {
            this.dataState = DataState.Error;
            return;
        }

        this.disableProceedButton = false;
        this.footerExtraButtonActions[FIRST_ELEMENT_INDEX].disabled = false;

        this.currentAnnualReport = cloneDeep<IAnnualReport>(annualReport);

        // index 1 for income to get annual profit
        const incomeTemplateAndAccountsMapping =
            finStatements[templateConstants.incomeTemplate].templateAndAccountsMapping;
        // setting current annual profit
        if (this.currentAnnualProfitValueForPrint != null) {
            this.currentAnnualProfit = NumberUtils.roundNumber(this.currentAnnualProfitValueForPrint);
        } else if (
            incomeTemplateAndAccountsMapping != null &&
            incomeTemplateAndAccountsMapping.template != null &&
            incomeTemplateAndAccountsMapping.template.group.calculatedClosingBalance != null
        ) {
            // REV-20899: include the aggregated transactional reclass on the
            // income statement group. `applyReclassificationsToFinStatements`
            // stamps `closingReClass` on every node (leaf + aggregate) for
            // v1 ARs, so the annual profit total picks up any reclass that
            // moved value into / out of the income statement.
            const incomeClosingReclass =
                (incomeTemplateAndAccountsMapping.template.group as { closingReClass?: number }).closingReClass ?? 0;
            this.currentAnnualProfit = NumberUtils.roundNumber(
                incomeTemplateAndAccountsMapping.template.group.calculatedClosingBalance + incomeClosingReclass,
            );
        }
        // setting previous annual profit
        if (this.previousAnnualProfitValueForPrint != null) {
            this.previousAnnualProfit = NumberUtils.roundNumber(this.previousAnnualProfitValueForPrint);
        } else if (
            incomeTemplateAndAccountsMapping != null &&
            incomeTemplateAndAccountsMapping.template != null &&
            incomeTemplateAndAccountsMapping.template.group.calculatedOpeningBalance != null
        ) {
            const incomeOpeningReclass =
                (incomeTemplateAndAccountsMapping.template.group as { openingReClass?: number }).openingReClass ?? 0;
            this.previousAnnualProfit = NumberUtils.roundNumber(
                incomeTemplateAndAccountsMapping.template.group.calculatedOpeningBalance + incomeOpeningReclass,
            );
        }

        const balanceTemplate = finStatements[templateConstants.balanceTemplate].templateAndAccountsMapping.template;
        this.balanceTemplate = balanceTemplate as IAccountMappingTemplate;
        this.mappedBalanceAccounts = this.accountMapper.getMappedAccountsOfBalanceTemplate(
            balanceTemplate as IAccountMappingTemplate,
        );
        this.equityAccounts = this.accountMapper.getAccountsOfEquityGroup(
            balanceTemplate as IAccountMappingTemplate,
        );

        if (this.currentAnnualReport.generalInformation != null) {
            this.currentYearDate = this.currentAnnualReport.generalInformation.balanceSheetDate;
            this.previousYearDate = annualReport.generalInformation.noPreviousData
                ? NO_PREVIOUS_DATE_PLACEHOLDER
                : annualReport.generalInformation.balancePreviousYearDate;
        }

        this.dataState = DataState.Success;
    }

    private handleAnnualReportForAutoFill(annualReport: IAnnualReport): void {
        if (annualReport?.appropriationOfProfit) {
            this.previousProfitCalculation = annualReport.appropriationOfProfit.profitCalculationOutput;
            this.previousProposal = annualReport.appropriationOfProfit.proposalOutput;
            this.aopDescription = annualReport.appropriationOfProfit.aopDescription;
            this.previousCcr = annualReport.appropriationOfProfit.ccrOutput;
            this.previousShowCcrTable = annualReport.appropriationOfProfit.showCcrTable;
        }
    }

    getAppropriationData(): IAppropriationOfProfit | undefined {
        return this.currentAnnualReport?.appropriationOfProfit ?? this.aopFromAutofill;
    }

    proceedButtonClicked = (): void => {
        this.save();
    };

    override save(): Observable<void> {
        this.mixpanelService.track(MIXPANEL_EVENTS.AOP_TIME_SPENT_TILL_COMPLETION);
        this.isSaveRequestInProgress = true;
        this.isSavedBefore = true;
        // The component hands its output over after init; a save fired before that must keep the stored
        // appropriation instead of overwriting it with `undefined`.
        if (this.appropriationOfProfitOutput != null) {
            this.currentAnnualReport.appropriationOfProfit = this.appropriationOfProfitOutput;
        }
        this.dispatchSaveAnnualReport(this.currentAnnualReport);
        return of(void 0);
    }

    protected onSaveSuccessfully(): void {
        if (this.isSaveRequestInProgress) {
            this.isSaveRequestInProgress = false;
            if (this.appropriationOfProfitOutput != null) {
                this.currentAnnualReport.appropriationOfProfit = this.appropriationOfProfitOutput;
            }
            this.savedSuccessfully.next(true);
            this.fireSuccessNotification();
        }
    }

    private onSaveFailed(): void {
        this.isSaveRequestInProgress = false;
        this.savedSuccessfully.next(false);
        this.fireErrorMessage();
    }

    protected override isDataNewlyCreatedAndSaveable(): boolean {
        if (this.currentAnnualReport && this.currentAnnualReport.appropriationOfProfit == null && this.disableProceedButton === false) {
            return true;
        }
        return false;
    }


    protected override isDataChanged(): Observable<boolean> {
        if (this.isSaveRequestInProgress) {
            return this.savedSuccessfully.pipe(map((savedSuccessfully) => !savedSuccessfully));
        } else {
            const savedAppropriationOfProfit = this.currentAnnualReport?.appropriationOfProfit ?? null;
            const currentAppropriationOfProfitOutput = this.appropriationOfProfitOutput ?? null;
            const isCurrentAopEqualSavedAop = fastDeepEqual(
                JSON.stringify(savedAppropriationOfProfit),
                JSON.stringify(currentAppropriationOfProfitOutput),
            );
            return of(!isCurrentAopEqualSavedAop);
        }
    }

    private removeUndefinedFields(appropriationOfProfit: IAppropriationOfProfit): IAppropriationOfProfit {
        if (
            appropriationOfProfit != null &&
            appropriationOfProfit.profitCalculationOutput != null &&
            appropriationOfProfit.profitCalculationOutput.account == null
        ) {
            delete appropriationOfProfit.profitCalculationOutput.account;
        }
        return appropriationOfProfit;
    }

    private setBalanceAccountsFromMapping(): void {
        const balanceTemplate =
            this.finStatements[templateConstants.balanceTemplate].templateAndAccountsMapping.template;
        this.balanceTemplate = balanceTemplate as IAccountMappingTemplate;
        this.mappedBalanceAccounts = this.accountMapper.getMappedAccountsOfBalanceTemplate(
            balanceTemplate as IAccountMappingTemplate,
        );
        this.equityAccounts = this.accountMapper.getAccountsOfEquityGroup(
            balanceTemplate as IAccountMappingTemplate,
        );
    }

    private getUpdatedAccount(profitCalculationOutput: IAopProfitCalculation): IAccount {
        const existingAccount = profitCalculationOutput.account?.accountNumber;
        const updatedAccount = this.mappedBalanceAccounts.find(
            (mappedAccount) => mappedAccount.accountNumber === existingAccount,
        );
        return updatedAccount;
    }
}
